import type { PostPreviewSizes } from '../../types';

// Constants extracted from Body.loading.tsx to keep component lean.
// Provides skeleton layout definitions for the facebookPost Body loading state.

export type SkeletonSizeMap = Readonly<Record<PostPreviewSizes, string>>;

// Helper builders to reduce repetition and improve readability.
const sizeMap = (small: string, medium: string, large: string): SkeletonSizeMap => ({
	xsmall: '',
	small,
	medium,
	large,
});
const rect = (key: string, sizes: SkeletonSizeMap) => ({ key, sizes }) as const;
const paragraph = (key: string, rectangles: ReadonlyArray<ReturnType<typeof rect>>, gaps: SkeletonSizeMap) =>
	({ key, gaps, rectangles }) as const;

export const bodySkeletonGaps: SkeletonSizeMap = sizeMap('gap-2', 'gap-[15px]', 'gap-[18px]');

// Paragraph rectangle specs defined as tuples for concise data declaration.
const paragraphDefs: ReadonlyArray<{
	key: string;
	rectangles: ReadonlyArray<ReturnType<typeof rect>>;
}> = [
	{
		key: 'paragraph1',
		rectangles: [
			rect('paragraph1-rectangle1', sizeMap('h-[5px] w-[80px]', 'h-[9px] w-[162px]', 'h-[11px] w-[193px]')),
			rect('paragraph1-rectangle2', sizeMap('h-[5px] w-[90px]', 'h-[9px] w-[178px]', 'h-[11px] w-[211px]')),
		],
	},
	{
		key: 'paragraph2',
		rectangles: [
			rect('paragraph2-rectangle1', sizeMap('h-[5px] w-[74px]', 'h-[9px] w-[154px]', 'h-[11px] w-[182px]')),
			rect('paragraph2-rectangle2', sizeMap('h-[5px] w-[80px]', 'h-[9px] w-[162px]', 'h-[11px] w-[193px]')),
			rect('paragraph2-rectangle3', sizeMap('h-[5px] w-[90px]', 'h-[9px] w-[178px]', 'h-[11px] w-[211px]')),
			rect('paragraph2-rectangle4', sizeMap('h-[5px] w-[70px]', 'h-[9px] w-[144px]', 'h-[11px] w-[170px]')),
			rect('paragraph2-rectangle5', sizeMap('h-[5px] w-[78px]', 'h-[9px] w-[169px]', 'h-[11px] w-[201px]')),
			rect('paragraph2-rectangle6', sizeMap('h-[5px] w-[20px]', 'h-[9px] w-[68px]', 'h-[11px] w-[81px]')),
		],
	},
	{
		key: 'paragraph3',
		rectangles: [
			rect('paragraph3-rectangle1', sizeMap('h-[5px] w-[80px]', 'h-[9px] w-[162px]', 'h-[11px] w-[193px]')),
			rect('paragraph3-rectangle2', sizeMap('h-[5px] w-[90px]', 'h-[9px] w-[178px]', 'h-[11px] w-[211px]')),
		],
	},
	{
		key: 'paragraph4',
		rectangles: [
			rect('paragraph4-rectangle1', sizeMap('h-[5px] w-[66px]', 'h-[9px] w-[134px]', 'h-[11px] w-[166px]')),
			rect('paragraph4-rectangle2', sizeMap('h-[5px] w-[90px]', 'h-[9px] w-[178px]', 'h-[11px] w-[211px]')),
			rect('paragraph4-rectangle3', sizeMap('h-[5px] w-[70px]', 'h-[9px] w-[144px]', 'h-[11px] w-[178px]')),
			rect('paragraph4-rectangle4', sizeMap('h-[5px] w-[17px]', 'h-[9px] w-[61px]', 'h-[11px] w-[75px]')),
		],
	},
];

const paragraphGaps = sizeMap('gap-[3px]', 'gap-1.5', 'gap-1.5');

const paragraphs = paragraphDefs.map(({ key, rectangles }) => paragraph(key, rectangles, paragraphGaps));

export const bodyShapes = {
	gaps: sizeMap('gap-2.5', 'gap-5', 'gap-[23px]'),
	textSkeletons: { paragraphs },
	imageSkeleton: { sizes: sizeMap('h-[53px] w-full', 'h-[112px] w-full', 'h-[131px] w-full') },
} as const;
